import type { Agenda, Document, Meeting } from "@repo/types";
import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "@repo/ui/components/Base/Input";
import { TextArea } from "@repo/ui/components/Base/TextArea";
import { Button } from "@repo/ui/components/Base/Button";
import { TagSelection } from "../tags";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { TimeField } from "@repo/ui/components/Base/TimeField";
import { DatePicker } from "@repo/ui/components/Base/DatePicker";
import React, { useContext, useEffect, useState } from "react";
import { addMinutes, differenceInMinutes } from "date-fns";
import { Time } from "@internationalized/date";
import { AgendaPreview } from "../agendaTemplate/AgendaPreview";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { RoleProtected } from "../common/RoleProtected";
import { useUserStore } from "@/stores/userStore";
import { cn } from "@repo/shared/utils";
import { CheckboxGroupInput } from "@repo/ui/components/Base/CheckboxGroupInput";
import {
  useCreateTrackedNewObjects,
  deleteTrackedNewObjects,
  useTrackedNewObjects,
} from "@/hooks/trackCreatedObjects";
import { ConfirmDeleteOnCancelDialog } from "./ConfirmDeleteOnCancelDialog";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { useTenantStore } from "@/stores/tenantStore";
import { AddressFormField } from "../users/AddressFormField";
import { Separator } from "@repo/ui/components/Base/Separator";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { apiService } from "@/services/apiService";
import { MeetingDefaultsContext } from "./MeetingDefaults";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { MEETING_MOTIF } from "@/lib/constants";
import { useDebounce } from "@repo/ui/components/Base/hooks/useDebounce";
import { dateSetDate, dateSetTime } from "@repo/lib/dateSet";
import { useGetMeetingTypes } from "@/hooks/queryHooks/configHooks";
import { useLetterHeadList } from "@/hooks/queryHooks/letterHeadHooks";
import { useGetTeam } from "@/hooks/queryHooks/teamHooks";
import { agendaRepo } from "@/repositories/agendaRepo";
import { meetingRepo } from "@/repositories/meetingRepo";
import { documentRepo } from "@/repositories/documentRepo";
import { teamRepo } from "@/repositories/teamRepo";

export interface CapturedCancel {
  /** 'cancel' is assigned within the MeetingForm component. Call it if available instead of closing popup. */
  cancel?: () => void;
  /** 'close' is passed down as a prop. This method should close the popup without inquiring of cancel. */
  close: () => void;
}

/** Gets the time difference between two date objects, in minutes. */
const calcMeetingDuration = (startTime: Date, endTime: Date) => {
  return differenceInMinutes(endTime, startTime);
};

export interface MeetingFormProps {
  meetingFormData?: Meeting;
  tenantId: string;
  handleCancel: () => void;
  handleComplete: () => void;
  handleGoBack?: () => void;
  onSubmit?: (meeting: Meeting) => void;
  capturedCancel?: CapturedCancel;
  hidePreview?: boolean;
  editAgenda?: (agenda: Agenda) => void;
  viewOnly?: boolean;
  allowAugmentedPDF?: boolean;
  withinDialog?: boolean;
}

export const MeetingForm = ({
  meetingFormData,
  tenantId,
  handleCancel,
  handleComplete,
  handleGoBack,
  onSubmit,
  capturedCancel,
  hidePreview,
  editAgenda,
  viewOnly = false,
  allowAugmentedPDF = false,
  withinDialog,
}: MeetingFormProps) => {
  useBlockUnload(!viewOnly);

  const { activeTeam, userId } = useUserStore((s) => ({
    activeTeam: s.activeTeam,
    userId: s.id,
  }));
  const tenant = useTenantStore((s) => s.tenant);

  const { data: letterheads } = useLetterHeadList(tenantId);
  const { data: meetingTypes } = useGetMeetingTypes();
  const { data: meetingTeam } = useGetTeam(
    tenantId,
    meetingFormData?.teamId ??
      (activeTeam === "org" ? "" : activeTeam?.id ?? "")
  );

  // track newly created objects and delete them
  const meetingHadAgenda = !!meetingFormData?.agendaId;
  const trackedNewObjects = useCreateTrackedNewObjects();
  const trackNew = useTrackedNewObjects();

  const validationSchema = yup.object({
    name: yup.string().required("Meeting name is required"),
  });

  const [pdfKey, setPdfKey] = useState(0);
  const refreshPdf = useDebounce(() => setPdfKey((key) => key + 1), 1000);

  useEffect(() => {
    refreshPdf();
  }, [meetingFormData, refreshPdf]);

  const handleReturn = () => (viewOnly ? handleGoBack?.() : handleCancel());

  const [augmented, setAugmented] = useState(false);

  const [confirmDeleteOnCancelOpen, setConfirmDeleteOnCancelOpen] =
    useState(false);

  const [isCanceling, setIsCanceling] = useState(false);

  if (capturedCancel) {
    capturedCancel.cancel = () => {
      if (meetingHadAgenda || !trackedNewObjects.length) {
        capturedCancel.close();
      } else {
        setConfirmDeleteOnCancelOpen(true);
      }
    };
  }

  const canViewConfidential = teamRepo.canViewConfidentialDocuments(
    userId,
    meetingTeam,
    true
  );

  const meetingDocuments =
    meetingFormData?.agenda?.agendaModules?.reduce(
      (array, agendaModule) => [
        ...array,
        ...(agendaModule.agendaItems?.reduce(
          (array, agendaItem) => [...array, ...(agendaItem.documents ?? [])],
          [] as Document[]
        ) ?? []),
      ],
      [] as Document[]
    ) ?? ([] as Document[]);

  const meetingDocumentUrls = meetingDocuments
    .map((doc) => documentRepo.getLinkedAttachment(doc)?.url ?? "")
    .join("\n");

  const [substituteLinkMap, setSubstituteLinkMap] = useState<Record<
    string,
    string
  > | null>(null);

  useEffect(() => {
    let cancel = false;

    (async () => {
      const linkMap = Object.create(null) as Record<string, string>;

      if (!hidePreview && canViewConfidential) {
        const agendaModules = meetingFormData?.agenda?.agendaModules ?? [];

        for (const agendaModule of agendaModules) {
          for (const agendaItem of agendaModule.agendaItems ?? []) {
            for (const document of agendaItem.documents ?? []) {
              const attachment = document.linkedAttachment
                ? document.attachments.find(
                    (attachment) =>
                      attachment.attachmentId === document.linkedAttachment
                  )
                : undefined;

              if (document.confidential && attachment?.url) {
                await apiService.v1
                  .documentDownloadUrl(tenantId, document.id)
                  .then(({ data: url }) => {
                    if (url) linkMap[attachment.url] = url;
                  });
                if (cancel) return;
              }
            }
          }
        }

        if (cancel) return;
      }
      setSubstituteLinkMap(linkMap);
    })();

    return () => {
      cancel = true;
    };
  }, [
    hidePreview,
    tenantId,
    meetingFormData,
    meetingDocumentUrls,
    canViewConfidential,
  ]);

  const defaults = useContext(MeetingDefaultsContext);

  if (!activeTeam || activeTeam === "org") return null;

  const defaultStartTime = activeTeam.defaultStartTime ?? {
    hours: 10,
    minutes: 0,
  };
  const defaultEndTime = activeTeam.defaultEndTime ?? { hours: 11, minutes: 0 };
  const now = new Date();

  return (
    <Formik
      initialValues={{
        tags: [],
        ...(meetingFormData ??
          meetingRepo.init({
            startTime: dateSetTime(
              now,
              defaultStartTime.hours,
              defaultStartTime.minutes
            ),
            endTime: dateSetTime(
              now,
              defaultEndTime.hours,
              defaultEndTime.minutes
            ),
            ...defaults,
            teamId: activeTeam.id,
          })),
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        if (!tenantId) {
          console.log("No tenant ID", tenantId);
          return;
        }

        const create = !meetingFormData;

        // if we're creating a new meeting, always create and attach a new agenda
        if (create) {
          const newAgenda = await agendaRepo.create(tenantId, {
            status: "draft",
            name: values.name,
            description: "",
            agendaModuleIds: [],
          });

          if (newAgenda) values.agendaId = newAgenda?.id;
        }

        const updatedMeeting = create
          ? await meetingRepo.create(tenantId, { ...values })
          : await meetingRepo.update(tenantId, values.id, { ...values });
        if (create) trackNew("meeting", updatedMeeting); // track created meetings
        trackedNewObjects.length = 0; // clear tracked new objects; submission confirms their creation

        if (updatedMeeting) onSubmit?.(updatedMeeting);
        handleComplete();
      }}
      enableReinitialize={!!editAgenda}
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange: rawHandleChange,
        setFieldValue: rawSetFieldValue,
        errors,
        touched,
        isValid,
        isSubmitting,
      }) => {
        const availableMeetingOptions = () => {
          const meetingType = meetingTypes?.meetingTypes.filter(
            (meetingType) => values.meetingType === meetingType.value
          );
          if (meetingType && meetingType?.length > 0) {
            return meetingType[0].options;
          }
          return;
        };

        const meetingOptions = availableMeetingOptions();

        const startTime = values.startTime;
        const endTime = values.endTime;

        const footer = (
          <>
            <Button
              variant="destructive"
              type="button"
              disabled={isSubmitting || isCanceling}
              onClick={handleReturn}
            >
              {viewOnly ? "Go Back" : "Cancel"}
            </Button>
            {!viewOnly && (
              <RoleProtected minRole="edit">
                <Button
                  type="submit"
                  disabled={!isValid || isSubmitting || isCanceling}
                >
                  Save
                </Button>
              </RoleProtected>
            )}
          </>
        );

        const setFieldValue = (name: string, value: unknown) => {
          refreshPdf();
          return rawSetFieldValue(name, value);
        };
        const handleChange = (event: React.ChangeEvent) => {
          rawHandleChange(event);
          refreshPdf();
        };

        const form = (
          <div className="flex flex-row gap-4 flex-grow">
            <div className={cn(hidePreview ? "w-full" : "w-1/4")}>
              <div className="flex flex-col gap-y-4">
                <SelectInput
                  disabled={viewOnly || isSubmitting}
                  label="Meeting Type"
                  value={values.meetingType}
                  onValueChange={(value) => {
                    // convert meetingType value to label
                    const typeLabel = (meetingType: string) =>
                      meetingTypes?.meetingTypes?.find(
                        (type) => type.value === meetingType
                      )?.label;

                    // if meeting title is not set or equals previous meetingType label, change it to new meetingType label
                    if (
                      !values.name ||
                      values.name === typeLabel(values.meetingType ?? "")
                    ) {
                      setFieldValue("name", typeLabel(value));
                    }

                    setFieldValue("meetingType", value);
                    setFieldValue("meetingTypeOption", "");

                    refreshPdf();
                  }}
                  options={
                    meetingTypes?.meetingTypes
                      ? meetingTypes?.meetingTypes.map((meetingType) => (
                          <SelectItem
                            key={meetingType.value}
                            value={meetingType.value}
                          >
                            {meetingType.label}
                          </SelectItem>
                        ))
                      : []
                  }
                />
                {meetingOptions &&
                  (() => {
                    switch (meetingOptions.type) {
                      case "checkbox":
                        return (
                          <CheckboxGroupInput
                            disabled={viewOnly || isSubmitting}
                            wrap
                            label={meetingOptions.optionsLabel}
                            options={meetingOptions.options.map((option) => ({
                              key: option.value,
                              label: option.label,
                            }))}
                            value={
                              values.meetingTypeOption
                                ? typeof values.meetingTypeOption === "string"
                                  ? [values.meetingTypeOption]
                                  : values.meetingTypeOption
                                : []
                            }
                            onValueChange={(value) => {
                              setFieldValue("meetingTypeOption", value);
                              refreshPdf();
                            }}
                          />
                        );

                      default:
                        return (
                          <SelectInput
                            disabled={viewOnly || isSubmitting}
                            label={meetingOptions?.optionsLabel}
                            value={
                              values.meetingTypeOption
                                ? typeof values.meetingTypeOption === "string"
                                  ? values.meetingTypeOption
                                  : values.meetingTypeOption[0]
                                : undefined
                            }
                            onValueChange={(value) => {
                              setFieldValue("meetingTypeOption", value);
                              refreshPdf();
                            }}
                            options={
                              meetingOptions?.options
                                ? meetingOptions.options.map((option) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))
                                : []
                            }
                          />
                        );
                    }
                  })()}
                <Input
                  disabled={viewOnly || isSubmitting}
                  label="*Meeting Title"
                  id="name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.name && touched.name}
                  errorMessage={errors.name}
                />
                <TextArea
                  disabled={viewOnly || isSubmitting}
                  label="Additional Text"
                  id="description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.description && touched.description}
                  errorMessage={errors.description}
                />
                {values.agenda && !values.agenda.letterHeadId && (
                  <SelectInput
                    disabled={viewOnly || isSubmitting}
                    label="Letterhead"
                    value={
                      values.agenda?.letterHeadId ?? values.letterHeadId ?? ""
                    }
                    onValueChange={(value) => {
                      setFieldValue("letterHeadId", value);
                      setFieldValue(
                        "letterHead",
                        letterheads?.find((lh) => lh.id === value)
                      );
                      refreshPdf();
                    }}
                    options={
                      letterheads
                        ? letterheads.map((lh) => (
                            <SelectItem key={lh.id} value={lh.id}>
                              {lh.viewName ?? lh.name}
                            </SelectItem>
                          ))
                        : []
                    }
                  />
                )}
                <div className="flex gap-y-1 mt-1.5 flex-col">
                  <DatePicker
                    disabled={viewOnly || isSubmitting}
                    id="startTime"
                    label="Date / Time"
                    date={startTime}
                    onDateChange={(date) => {
                      if (!date) return;

                      const start = values.startTime ?? new Date();
                      const end = values.endTime ?? new Date();

                      date = dateSetDate(
                        start,
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      );
                      setFieldValue("startTime", date);

                      const newEnd = dateSetDate(
                        end,
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      );
                      setFieldValue("endTime", newEnd);
                    }}
                  />
                  <div className="flex flex-row gap-2 items-center">
                    <span>From</span>
                    <TimeField
                      disabled={viewOnly || isSubmitting}
                      disableHasTimeChange
                      value={
                        new Time(startTime?.getHours(), startTime?.getMinutes())
                      }
                      onChange={(time) => {
                        const start = startTime ?? new Date();

                        const newStart = dateSetTime(
                          start,
                          time.hour,
                          time.minute
                        );
                        setFieldValue("startTime", newStart);
                        setFieldValue(
                          "endTime",
                          addMinutes(
                            newStart,
                            calcMeetingDuration(startTime, endTime)
                          )
                        );
                      }}
                    />
                    <span>To</span>
                    <TimeField
                      disabled={viewOnly || isSubmitting}
                      disableHasTimeChange
                      // hasTime={endTimeSet}
                      value={
                        new Time(endTime?.getHours(), endTime?.getMinutes())
                      }
                      onChange={(time) => {
                        const end = endTime ?? new Date();

                        const newEnd = dateSetTime(end, time.hour, time.minute);
                        setFieldValue("endTime", newEnd);
                      }}
                      // onHasTimeChange={setEndTimeSet}
                    />
                  </div>
                </div>

                <Separator />

                <p className="text-slate-800 text-base leading-5">
                  Address Held:
                </p>

                <AddressFormField
                  disabled={viewOnly || isSubmitting}
                  address={values.address ?? meetingTeam?.address}
                  fieldName="address"
                  setFieldValue={setFieldValue}
                  singleType
                />

                <TagSelection
                  disabled={viewOnly || isSubmitting}
                  label="Tags"
                  value={values.tags}
                  onChange={(newTags) => setFieldValue("tags", newTags)}
                  enableAutocomplete={true}
                  useStandardizedTags={true}
                />

                {!withinDialog && (
                  <div className="flex flex-row justify-between">{footer}</div>
                )}
              </div>
            </div>
            {!hidePreview && (
              <div className="w-full flex-grow flex flex-col">
                {substituteLinkMap && meetingTeam ? (
                  <AgendaPreview
                    className="border border-gray-300"
                    key={"key:" + pdfKey + ":" + augmented}
                    showToolbar={true}
                    tenant={tenant}
                    meeting={{
                      ...values,
                      ...(meetingFormData
                        ? {
                            agenda: meetingFormData.agenda,
                            agendaId: meetingFormData.agendaId,
                          }
                        : null),
                    }}
                    team={meetingTeam}
                    letterHead={
                      values.agenda?.letterHead ??
                      values.letterHead ??
                      undefined
                    }
                    enableLinks={canViewConfidential ? "all" : true}
                    substituteLinkMap={
                      canViewConfidential ? substituteLinkMap : undefined
                    }
                    augmented={allowAugmentedPDF && augmented}
                  />
                ) : (
                  <div className="flex flex-col justify-center w-full m-auto items-center">
                    <Spinner />
                  </div>
                )}
                {allowAugmentedPDF && (
                  <div className="w-full mt-5">
                    <CheckboxInput
                      containerStyle="justify-center"
                      orientation="horizontal"
                      label="Show additional details on agenda"
                      checked={augmented}
                      onCheckedChange={(checked) => setAugmented(!!checked)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );

        return (
          <form onSubmit={handleSubmit}>
            <ConfirmDeleteOnCancelDialog
              open={confirmDeleteOnCancelOpen}
              setOpen={setConfirmDeleteOnCancelOpen}
              headerClassName={MEETING_MOTIF}
              trackedNewObjects={trackedNewObjects}
              onConfirmDelete={async () => {
                setIsCanceling(true);
                await deleteTrackedNewObjects(trackedNewObjects);
                setIsCanceling(false);
                capturedCancel?.close();
              }}
              onKeepCreated={() => {
                capturedCancel?.close();
              }}
              onReturnToEditing={() => {
                setConfirmDeleteOnCancelOpen(false);
              }}
            />

            {withinDialog ? (
              <>
                <DialogBody>{form}</DialogBody>
                <DialogFooter className="justify-between">
                  {footer}
                </DialogFooter>
              </>
            ) : (
              form
            )}
          </form>
        );
      }}
    </Formik>
  );
};
